import { useCallback, useEffect, useRef, useState } from 'react'

/** Full-width scroll-snap track: one slide per view, swipe + programmatic navigation. */
export function useSlideTrack(count: number) {
  const trackRef = useRef<HTMLUListElement>(null)
  const [active, setActive] = useState(0)

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        const width = track.clientWidth
        if (!width) return
        setActive(Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / width))))
      })
    }
    track.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      track.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [count])

  const goTo = useCallback(
    (index: number, instant = false) => {
      const track = trackRef.current
      if (!track) return
      const next = Math.max(0, Math.min(count - 1, index))
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      track.scrollTo({ left: next * track.clientWidth, behavior: instant || reduce ? 'auto' : 'smooth' })
      setActive(next)
    },
    [count],
  )

  return { trackRef, active, goTo }
}
